/**
 * Consent Banner Component
 *
 * GDPR-compliant consent banner for analytics tracking.
 * Shown to users who haven't made a consent choice yet.
 */

import { useState } from 'react';
import { Shield, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useConsentStatus, useUpdateAnalyticsConsent } from '@have-done/shared';

interface ConsentBannerProps {
  onConsentChange?: (accepted: boolean) => void;
}

export function ConsentBanner({ onConsentChange }: ConsentBannerProps) {
  const consentStatus = useConsentStatus();
  const updateConsent = useUpdateAnalyticsConsent();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Don't show if consent already given or banner dismissed
  if (
    isDismissed ||
    consentStatus === undefined || // Loading
    consentStatus === null || // Not authenticated
    (consentStatus?.analytics !== 'pending' && !consentStatus?.needsReconsent)
  ) {
    return null;
  }

  const handleAccept = async () => {
    setIsUpdating(true);
    try {
      await updateConsent({ analytics: 'accepted' });
      onConsentChange?.(true);
    } catch (error) {
      console.error('Failed to update consent:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReject = async () => {
    setIsUpdating(true);
    try {
      await updateConsent({ analytics: 'rejected' });
      onConsentChange?.(false);
    } catch (error) {
      console.error('Failed to update consent:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    // Dismissing without choice = reject (GDPR: no consent = no tracking)
    handleReject();
    setIsDismissed(true);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
    >
      <div className="mx-auto max-w-3xl">
        <div className="rounded-xl bg-bg-primary border border-border-light shadow-warm-lg p-4 sm:p-6">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="hidden sm:flex shrink-0 w-10 h-10 rounded-full bg-primary/10 items-center justify-center">
              <Shield className="w-5 h-5 text-primary" aria-hidden="true" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-text-primary mb-1">
                Your Privacy Matters
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                We use analytics to understand how you use Have-Done and improve your experience.
                This data is anonymized and never sold.{' '}
                <Link
                  to="/privacy"
                  className="text-primary hover:text-primary-dark underline"
                >
                  Learn more
                </Link>
              </p>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={isUpdating}
                  className="
                    px-4 py-2 rounded-lg
                    text-sm font-medium text-white
                    bg-primary hover:bg-primary-dark
                    transition-colors
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  {isUpdating ? 'Saving...' : 'Accept Analytics'}
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={isUpdating}
                  className="
                    px-4 py-2 rounded-lg
                    text-sm font-medium text-text-primary
                    bg-bg-secondary hover:bg-bg-tertiary
                    transition-colors
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  Reject
                </button>
              </div>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={handleDismiss}
              disabled={isUpdating}
              aria-label="Dismiss"
              className="
                shrink-0 p-1.5 rounded-lg
                text-text-tertiary hover:text-text-primary hover:bg-bg-secondary
                transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
